import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Plus, Layout, Eye, Edit, Cloud, CloudOff, Undo2, Redo2, BookMarked, ChevronDown, Pencil } from 'lucide-react';
import QuestionCard from '../../components/builder/QuestionCard';
import ThemeSelector from '../../components/builder/ThemeSelector';
import LogoUploader from '../../components/builder/LogoUploader';
import FormSettings from '../../components/builder/FormSettings';
import StyleSettings from '../../components/builder/StyleSettings';
import LogicBuilder from '../../components/builder/LogicBuilder';
import AIGeneratorModal from '../../components/dashboard/AIGeneratorModal';
import SaveAsTemplateModal from '../../components/dashboard/SaveAsTemplateModal';
import { useFormBuilder } from '../../hooks/useFormBuilder';
import { useSearchParams, useLocation } from 'react-router-dom';
import { getForm } from '../../services/formService';
import { hasLocalDraft } from '../../services/draftService';
import { useNavigate } from 'react-router-dom';
import { Feather } from 'lucide-react';
import { useFormBuilderStore } from '../../store/formBuilderStore';
import { useUserStore } from '../../store/userStore';
import toast from 'react-hot-toast';

const CollapsibleSidebarSection = ({ title, children, defaultOpen = false, className = '', rightElement = null }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <section className={`rounded-2xl border border-gray-200 bg-white/80 backdrop-blur transition-all overflow-hidden ${className}`}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between p-4 bg-transparent hover:bg-gray-50/50 transition-colors text-left group"
      >
        <div className="flex items-center gap-2">
            <h2 className="text-sm font-semibold text-gray-900">{title}</h2>
        </div>
        <div className="flex items-center gap-2">
            {rightElement}
            <ChevronDown 
            className={`h-4 w-4 text-gray-400 group-hover:text-gray-600 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
            />
        </div>
      </button>
      
      {isOpen && (
        <div className="p-4 pt-0">
          {children}
        </div>
      )}
    </section>
  );
};

const BuilderMain = () => {
  const {
    title,
    description,
    sections,
    questions,
    addQuestion,
    addSection,
    updateFormInfo,
    updateSection,
    initForm,
    saveForm,
    isSaving,
    loadLocalDraft
  } = useFormBuilder();
  const { generateForm, isGenerating, isDirty, lastSavedAt, id: formId, undo, redo, canUndo, canRedo } = useFormBuilderStore((state) => ({
    generateForm: state.generateForm,
    isGenerating: state.isGenerating,
    isDirty: state.isDirty,
    lastSavedAt: state.lastSavedAt,
    id: state.id,
    undo: state.undo,
    redo: state.redo,
    canUndo: state.canUndo,
    canRedo: state.canRedo
  }));
  const { user } = useUserStore();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const initializedRef = useRef(false);
  const themeSectionRef = useRef(null);
  const [showFomzy, setShowFomzy] = useState(false);
  const [fomzyMode, setFomzyMode] = useState('create');
  const [showFomzyMenu, setShowFomzyMenu] = useState(false);
  const fomzyMenuRef = useRef(null);
  const [showFomzyOverlay, setShowFomzyOverlay] = useState(false);
  const [showSaveAsTemplate, setShowSaveAsTemplate] = useState(false);

  // Keyboard shortcuts for undo/redo
  const handleKeyDown = useCallback((e) => {
    // Check if user is typing in an input field
    if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA' || e.target.isContentEditable) {
      return;
    }
    
    if ((e.ctrlKey || e.metaKey) && e.key === 'z') {
      e.preventDefault();
      if (e.shiftKey) {
        // Redo: Ctrl+Shift+Z
        if (canRedo()) {
          redo();
          toast('Redo', { icon: '↪️', duration: 1500 });
        }
      } else {
        // Undo: Ctrl+Z
        if (canUndo()) {
          undo();
          toast('Undo', { icon: '↩️', duration: 1500 });
        }
      }
    }
    // Also support Ctrl+Y for redo
    if ((e.ctrlKey || e.metaKey) && e.key === 'y') {
      e.preventDefault();
      if (canRedo()) {
        redo();
        toast('Redo', { icon: '↪️', duration: 1500 });
      }
    }
  }, [undo, redo, canUndo, canRedo]);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  useEffect(() => {
    if (!initializedRef.current) {
      // If coming from AI generator, draft, or preview, don't reset the form
      if (location.state?.fromAI || location.state?.fromDraft || location.state?.fromPreview) {
        initializedRef.current = true;
        return;
      }

      // If coming from create page with title/description, set them
      if (location.state?.title || location.state?.description) {
        updateFormInfo({
          title: location.state.title || '',
          description: location.state.description || ''
        });
        initializedRef.current = true;
        return;
      }

      // Check for local draft (for non-logged-in users)
      if (!user && hasLocalDraft()) {
        loadLocalDraft();
        toast('Draft restored', { icon: '📝' });
        initializedRef.current = true;
        return;
      }

      // Initialize with new empty form
      initForm();
      initializedRef.current = true;
    }
  }, [initForm, location.state, updateFormInfo, user, loadLocalDraft]);

  useEffect(() => {
    const fid = searchParams.get('formId');
    if (fid) {
      // Load existing form from service and initialize builder store
      (async () => {
        try {
          const formDoc = await getForm(fid);
          if (formDoc) {
            initForm(formDoc);
          }
        } catch (error) {
          console.error('Failed to load form into builder', error);
        }
      })();
    }
  }, [searchParams, initForm]);

  const handlePreview = () => {
    navigate('/builder/preview');
  };

  const handleAddSection = () => {
    const sectionName = prompt('Enter section name:', 'New Section');
    if (sectionName && sectionName.trim()) {
      addSection(sectionName.trim());
    }
  };

  const handleChangeLayout = () => {
    themeSectionRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleEditSection = (sectionId, currentTitle) => {
    const newTitle = prompt('Edit section name:', currentTitle);
    if (newTitle && newTitle.trim() && newTitle.trim() !== currentTitle) {
      updateSection(sectionId, { title: newTitle.trim() });
    }
  };

  const buildFomzyFillPrompt = () => {
    const parts = [];
    const hasTitle = Boolean(title?.trim());
    const hasDescription = Boolean(description?.trim());
    const loose = questions.filter((q) => !q.sectionId);
    const sectionBlocks = sections.map((section) => ({
      title: section.title,
      description: section.description,
      questions: questions.filter((q) => q.sectionId === section.id),
    }));

    if (hasTitle) {
      parts.push(`Title: "${title.trim()}"`);
    }

    if (hasDescription) {
      parts.push(`Description: "${description.trim()}"`);
    }

    const listQuestions = (qs, label) =>
      qs.length
        ? `${label}: ` +
          qs
            .map(
              (q, idx) =>
                `${idx + 1}. [${q.type}] ${q.label || q.title || 'Untitled'}${q.required ? ' (required)' : ''}`
            )
            .join(' ')
        : '';

    const includeQuestions = !hasTitle || !hasDescription;

    if (!hasTitle) {
      parts.push('No title provided. Recommend adding a title before using Fomzy.');
    }

    if (!hasTitle && hasDescription) {
      parts.push('Use description as main context; fill gaps as needed.');
    }

    if (!hasTitle && !hasDescription && (sectionBlocks.length || loose.length)) {
      parts.push('No title/description provided. Derive context from questions.');
    }

    if (includeQuestions) {
      if (sectionBlocks.length) {
        sectionBlocks.forEach((section) => {
          const qs = section.questions || [];
          parts.push(
            `Section "${section.title || 'Untitled section'}": ${section.description || 'no description'}. ${listQuestions(
              qs,
              'Questions'
            )}`
          );
        });
      }
      if (loose.length) {
        parts.push(listQuestions(loose, 'General questions'));
      }
    }

    parts.push(
      'Return a complete form with sections, questions, and a fitting theme. If title is missing, propose one.'
    );
    return parts.filter(Boolean).join(' ');
  };

  const handleFomzyFill = async () => {
    setShowFomzyMenu(false);
    setShowFomzyOverlay(true);
    if (window?.navigator?.vibrate) {
      window.navigator.vibrate(10);
    }
    const hasTitle = Boolean(title?.trim());
    const hasDescription = Boolean(description?.trim());
    const hasQuestions = questions.length > 0;

    if (!hasTitle) {
      toast('Tip: Add a title so Fomzy can anchor the form.', { icon: '✏️' });
    }

    if (!hasTitle && !hasDescription && !hasQuestions) {
      toast('No title, description, or questions found. Fomzy will improvise a sensible start.', { icon: '✨' });
    }

    const prompt = buildFomzyFillPrompt();
    const result = await generateForm(prompt, !!user);
    
    if (result.error === 'LIMIT_REACHED') {
      toast.error('Daily AI limit reached! Sign in for unlimited generations.');
    } else if (!result.success) {
      toast.error('Failed to generate form. Please try again.');
    }
    
    setShowFomzyOverlay(false);
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (fomzyMenuRef.current && !fomzyMenuRef.current.contains(e.target)) {
        setShowFomzyMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <>
    <AIGeneratorModal
      isOpen={showFomzy}
      onClose={() => setShowFomzy(false)}
      mode={fomzyMode}
    />
    <div className="grid lg:grid-cols-[2fr,1fr] gap-4 animate-fade-in">
      <div className="space-y-4 animate-slide-up" style={{ animationDelay: '100ms' }}>
        <header className="rounded-2xl border border-gray-200 bg-white/80 backdrop-blur p-4 transition-all relative">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2 md:gap-3">
              <p className="text-[10px] md:text-xs uppercase tracking-normal text-gray-500 font-semibold">Form Builder</p>
              
              {/* Undo/Redo buttons */}
              <div className="flex items-center gap-0.5 md:gap-1 border-l border-gray-200 pl-2 md:pl-3">
                <button
                  onClick={() => { if (canUndo()) { undo(); toast('Undo', { icon: '↩️', duration: 1500 }); } }}
                  disabled={!canUndo()}
                  className="p-1 md:p-1.5 rounded-md text-gray-500 hover:text-gray-700 hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                  title="Undo (Ctrl+Z)"
                >
                  <Undo2 className="h-3 w-3 md:h-4 md:w-4" />
                </button>
                <button
                  onClick={() => { if (canRedo()) { redo(); toast('Redo', { icon: '↪️', duration: 1500 }); } }}
                  disabled={!canRedo()}
                  className="p-1 md:p-1.5 rounded-md text-gray-500 hover:text-gray-700 hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                  title="Redo (Ctrl+Shift+Z)"
                >
                  <Redo2 className="h-3 w-3 md:h-4 md:w-4" />
                </button>
              </div>
              
              {/* Auto-save status indicator */}
              <div className="flex items-center gap-1 md:gap-1.5 text-[10px] md:text-xs text-gray-400">
                {isDirty ? (
                  <>
                    <div className="h-1 md:h-1.5 w-1 md:w-1.5 rounded-full bg-amber-400 animate-pulse" />
                    <span>Saving...</span>
                  </>
                ) : lastSavedAt ? (
                  <>
                    {user ? (
                      <Cloud className="h-3 w-3 md:h-3.5 md:w-3.5 text-green-500" />
                    ) : (
                      <CloudOff className="h-3 w-3 md:h-3.5 md:w-3.5 text-gray-400" />
                    )}
                    <span className="text-green-600">
                      <span className="md:hidden">{formId ? 'Saved' : (user ? 'Draft' : 'Local')}</span>
                      <span className="hidden md:inline">{formId ? 'Saved' : (user ? 'Saved to drafts' : 'Saved locally')}</span>
                    </span>
                  </>
                ) : null}
              </div>
            </div>
            <button
              onClick={saveForm}
              disabled={isSaving}
              className="inline-flex items-center gap-1 md:gap-1.5 rounded-full bg-primary-600 px-2.5 py-1 text-[10px] md:px-3 md:py-1.5 md:text-sm font-medium text-white transition-all hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed active:scale-95"
            >
              {isSaving ? 'Saving...' : <><span className="md:hidden">Save</span><span className="hidden md:inline">Save Form</span></>}
            </button>
          </div>
          
          <input
            className="w-full font-display text-2xl font-bold text-gray-900 placeholder:text-gray-400 focus:outline-none bg-transparent border-b-2 border-transparent focus:border-gray-300 transition-colors pb-1"
            value={title}
            onChange={(e) => updateFormInfo({ title: e.target.value })}
            placeholder="Untitled form"
          />
          
          <textarea
            className="mt-2 w-full text-sm text-gray-600 placeholder:text-gray-400 focus:outline-none bg-transparent resize-none rounded-xl border border-transparent focus:border-gray-300 focus:bg-white transition-all p-2"
            value={description}
            onChange={(e) => updateFormInfo({ description: e.target.value })}
            placeholder="Add a description to your form..."
            rows={2}
          />
          
          <div className="mt-4 pt-4 border-t border-gray-100 flex flex-wrap gap-1.5 md:gap-2">
            <button
              onClick={handleAddSection}
              className="inline-flex items-center gap-1 md:gap-2 rounded-full border border-gray-200 px-2 py-1 text-xs md:px-3 md:py-1.5 md:text-sm text-gray-700 transition-all hover:bg-gray-50 active:scale-95"
            >
              <Plus className="h-3 w-3 md:h-4 md:w-4" />
              Add Section
            </button>
            <button
              onClick={handleChangeLayout}
              className="inline-flex items-center gap-1 md:gap-2 rounded-full border border-gray-200 px-2 py-1 text-xs md:px-3 md:py-1.5 md:text-sm text-gray-700 transition-all hover:bg-gray-50 active:scale-95"
            >
              <Layout className="h-3 w-3 md:h-4 md:w-4" />
              Change Layout
            </button>
            <button
              onClick={handlePreview}
              className="inline-flex items-center gap-1 md:gap-2 rounded-full border border-gray-200 px-2.5 py-1 text-xs md:px-4 md:py-2 md:text-sm text-gray-700 transition-all hover:border-gray-900 hover:bg-gray-50 active:scale-95"
            >
              <Eye className="h-3 w-3 md:h-4 md:w-4" />
              Preview
            </button>
            <button
              onClick={() => {
                if (!user) {
                  toast.error('Please sign in to save templates');
                  return;
                }
                if (questions.length === 0) {
                  toast.error('Add some questions first');
                  return;
                }
                setShowSaveAsTemplate(true);
              }}
              className="inline-flex items-center gap-1 md:gap-2 rounded-full border border-gray-200 px-2.5 py-1 text-xs md:px-4 md:py-2 md:text-sm text-gray-700 transition-all hover:border-amber-500 hover:bg-amber-50 active:scale-95"
              title="Save as Template"
            >
              <BookMarked className="h-3 w-3 md:h-4 md:w-4" />
              <span className="hidden md:inline">Save as Template</span>
            </button>
            <button
              onClick={() => addQuestion()}
              className="inline-flex items-center gap-1 md:gap-2 rounded-full border border-gray-900 bg-gray-900 px-2.5 py-1 text-xs md:px-4 md:py-2 md:text-sm text-white transition-all hover:bg-gray-800 hover:scale-105 active:scale-95"
            >
              <Plus className="h-3 w-3 md:h-4 md:w-4" />
              Add Question
            </button>
          </div>
        </header>

        <div className="space-y-6">
          {sections.length === 0 && questions.length === 0 ? (
            <div className="border border-dashed border-gray-300 rounded-xl p-8 text-center animate-slide-up" style={{ animationDelay: '200ms' }}>
              <h3 className="text-lg font-semibold text-gray-900">No sections or questions yet</h3>
              <p className="text-gray-500 mt-2">Add your first section or question to start building.</p>
              <div className="mt-4 flex gap-2 md:gap-3 justify-center">
                <button
                  className="inline-flex items-center gap-1 md:gap-2 rounded-full border border-gray-900 bg-gray-900 px-2.5 py-1 text-xs md:px-4 md:py-2 md:text-sm text-white transition-all hover:bg-gray-800 hover:scale-105 active:scale-95"
                  onClick={handleAddSection}
                >
                  <Plus className="h-3 w-3 md:h-4 md:w-4" />
                  Add Section
                </button>
                <button
                  className="inline-flex items-center gap-1 md:gap-2 rounded-full border border-gray-200 px-2.5 py-1 text-xs md:px-4 md:py-2 md:text-sm text-gray-700 transition-all hover:border-gray-900 hover:bg-gray-50 active:scale-95"
                  onClick={() => addQuestion()}
                >
                  <Plus className="h-3 w-3 md:h-4 md:w-4" />
                  Add Question
                </button>
              </div>
            </div>
          ) : (
            <>
              {/* Render sections */}
              {sections.map((section, sectionIndex) => {
                const sectionQuestions = questions.filter(q => q.sectionId === section.id);
                return (
                  <div key={section.id} className="rounded-3xl border border-gray-200/80 bg-white/80 backdrop-blur p-6 animate-slide-up transition-all hover:shadow-md" style={{ animationDelay: `${(sectionIndex + 1) * 100}ms` }}>
                    <div className="mb-4 flex items-center justify-between">
                      <div>
                        <h3 className="font-display text-xl text-gray-900">{section.title}</h3>
                        {section.description && <p className="text-sm text-gray-600 mt-1">{section.description}</p>}
                      </div>
                      <button
                        onClick={() => handleEditSection(section.id, section.title)}
                        className="inline-flex items-center gap-1 rounded-full border border-gray-200 px-1.5 py-0.5 text-[10px] md:px-2 md:py-1 md:text-xs text-gray-600 transition-all hover:border-gray-900 hover:bg-gray-50 active:scale-95"
                        title="Edit section name"
                      >
                        <Edit className="h-2.5 w-2.5 md:h-3 md:w-3" />
                        Edit
                      </button>
                    </div>
                    
                    <div className="space-y-3">
                      {sectionQuestions.length === 0 ? (
                        <div className="border border-dashed border-gray-200 rounded-xl p-6 text-center">
                          <p className="text-gray-500 text-sm">No questions in this section yet.</p>
                          <button
                            className="mt-2 inline-flex items-center gap-1 md:gap-2 rounded-full border border-gray-200 px-2 py-0.5 text-[10px] md:px-3 md:py-1 md:text-xs text-gray-600 transition-all hover:border-gray-900 hover:bg-gray-50 active:scale-95"
                            onClick={() => addQuestion('short-text', section.id)}
                          >
                            <Plus className="h-2.5 w-2.5 md:h-3 md:w-3" />
                            Add Question
                          </button>
                        </div>
                      ) : (
                        sectionQuestions.map((question) => (
                          <QuestionCard 
                            key={question.id} 
                            question={question} 
                            index={questions.findIndex(q => q.id === question.id)} 
                            total={questions.length} 
                          />
                        ))
                      )}
                    
                    </div>
                    
                    <div className="mt-4 pt-4 border-t border-gray-100 flex justify-between items-center">
                      <span className="text-[10px] md:text-xs text-gray-500">{sectionQuestions.length} questions</span>
                      <button
                        className="inline-flex items-center gap-1 md:gap-2 rounded-full border border-gray-200 px-2 py-0.5 text-[10px] md:px-3 md:py-1 md:text-xs text-gray-600 transition-all hover:border-gray-900 hover:bg-gray-50 active:scale-95"
                        onClick={() => addQuestion('short-text', section.id)}
                      >
                        <Plus className="h-2.5 w-2.5 md:h-3 md:w-3" />
                        Add Question
                      </button>
                    </div>
                  </div>
                );
              })}
              
              {/* Render questions not in sections */}
              {questions.filter(q => !q.sectionId).length > 0 && (
                <div className="rounded-3xl border border-gray-200/80 bg-white/80 backdrop-blur p-6 animate-slide-up" style={{ animationDelay: `${(sections.length + 1) * 100}ms` }}>
                  <div className="mb-4">
                    <h3 className="font-display text-xl text-gray-900">General Questions</h3>
                    <p className="text-sm text-gray-600 mt-1">Questions not assigned to any section</p>
                  </div>
                  
                  <div className="space-y-3">
                    {questions.filter(q => !q.sectionId).map((question) => (
                      <QuestionCard 
                        key={question.id} 
                        question={question} 
                        index={questions.findIndex(q => q.id === question.id)} 
                        total={questions.length} 
                      />
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <aside className="space-y-5 animate-slide-up" style={{ animationDelay: '300ms' }}>
        <section ref={themeSectionRef} className="rounded-2xl border border-gray-200 bg-white/80 backdrop-blur p-4 transition-all">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-semibold text-gray-900">Theme</h2>
            <span className="text-xs text-gray-400">Instant preview</span>
          </div>
          <ThemeSelector />
        </section>

        <section className="rounded-2xl border border-gray-200 bg-white/80 backdrop-blur p-4 transition-all">
          <h2 className="text-sm font-semibold text-gray-900 mb-3">Typography & Style</h2>
          <StyleSettings />
        </section>

        <section className="rounded-2xl border border-gray-200 bg-white/80 backdrop-blur p-4 transition-all">
          <h2 className="text-sm font-semibold text-gray-900 mb-3">Branding</h2>
          <LogoUploader />
        </section>

        <CollapsibleSidebarSection title="Conditional Logic">
          <LogicBuilder />
        </CollapsibleSidebarSection>

        <section className="rounded-2xl border border-gray-200 bg-white/80 backdrop-blur p-4 transition-all">
          <h2 className="text-sm font-semibold text-gray-900 mb-3">Form settings</h2>
          <FormSettings />
        </section>
      </aside>
    </div>
    <div className="fixed bottom-6 right-6 z-40 flex flex-col items-end" ref={fomzyMenuRef}>
      {showFomzyMenu && (
        <div
          className="mb-2 flex flex-col gap-1.5 md:gap-2 rounded-2xl border border-gray-200 bg-white p-2 md:p-3 shadow-xl transition-all duration-200 ease-out animate-fade-in-up"
        >
          <button
            className="inline-flex items-center gap-1.5 md:gap-2 rounded-full border border-violet-200 px-2.5 py-1.5 text-xs md:px-3 md:py-2 md:text-sm font-semibold text-violet-700 hover:border-violet-500 hover:bg-violet-50 active:scale-95 transition"
            onClick={() => {
              setShowFomzyMenu(false);
              setFomzyMode('edit');
              setShowFomzy(true);
            }}
            disabled={isGenerating}
          >
            <Pencil className="h-3 w-3 md:h-4 md:w-4" />
            Edit this form
          </button>
          <button
            className="inline-flex items-center gap-1.5 md:gap-2 rounded-full border border-sky-200 px-2.5 py-1.5 text-xs md:px-3 md:py-2 md:text-sm font-semibold text-sky-700 hover:border-sky-500 hover:bg-sky-50 active:scale-95 transition"
            onClick={() => {
              setShowFomzyMenu(false);
              setFomzyMode('create');
              setShowFomzy(true);
            }}
            disabled={isGenerating}
          >
            <Feather className="h-3 w-3 md:h-4 md:w-4 text-sky-600" />
            Create new
          </button>
          <button
            className="inline-flex items-center gap-1.5 md:gap-2 rounded-full border border-gray-200 px-2.5 py-1.5 text-xs md:px-3 md:py-2 md:text-sm font-semibold text-gray-800 hover:border-sky-200 hover:bg-sky-50 active:scale-95 transition"
            onClick={handleFomzyFill}
            disabled={isGenerating}
          >
            {isGenerating ? (
              <>
                <span className="inline-flex h-3 w-3 md:h-4 md:w-4 animate-spin rounded-full border-2 border-sky-500 border-t-transparent" />
                Building…
              </>
            ) : (
              <>
                <span className="h-1.5 w-1.5 md:h-2 md:w-2 rounded-full bg-sky-500"></span>
                Fill this form
              </>
            )}
          </button>
        </div>
      )}
      <button
        onClick={() => {
          if (window?.navigator?.vibrate) {
            window.navigator.vibrate(10);
          }
          setShowFomzyMenu((prev) => !prev);
        }}
        className={`inline-flex items-center gap-1.5 md:gap-2 rounded-full border border-sky-200 bg-white px-3 py-1.5 text-xs md:px-4 md:py-2 md:text-sm shadow-lg transition hover:-translate-y-0.5 hover:shadow-xl active:scale-95 ${showFomzyMenu ? 'animate-pulse' : ''}`}
        title="Ask Fomzy to help"
      >
        <Feather className="h-3 w-3 md:h-4 md:w-4 text-sky-600" />
        <span className="font-semibold text-gray-800">Fomzy</span>
      </button>
    </div>
    {showFomzyOverlay && (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
        <div className="rounded-full bg-white px-6 py-4 shadow-2xl flex items-center gap-3 border border-gray-200 animate-fade-in-up">
          <span className="inline-flex h-5 w-5 animate-spin rounded-full border-2 border-sky-500 border-t-transparent" />
          <div>
            <p className="text-sm font-semibold text-gray-900">Building with Fomzy</p>
            <p className="text-xs text-gray-600">Drafting sections, questions, and theme...</p>
          </div>
        </div>
      </div>
    )}
    <SaveAsTemplateModal
      isOpen={showSaveAsTemplate}
      onClose={() => setShowSaveAsTemplate(false)}
      form={{ title, description, questions }}
      onSaved={(template) => {
        toast.success(`Template "${template.name}" saved!`);
      }}
    />
    </>
  );
};

export default BuilderMain;






